<template>
  <div class="barra-controles">
    <!-- Navegación de mes -->
    <div class="nav-mes">
      <button class="btn-nav" @click="store.mesAnterior()" :disabled="store.cargando">
        <i class="bi bi-chevron-left"></i>
      </button>
      <span class="mes-label">{{ mesTitulo }}</span>
      <button class="btn-nav" @click="store.mesSiguiente()" :disabled="store.cargando">
        <i class="bi bi-chevron-right"></i>
      </button>
      <button class="btn-hoy" @click="store.irAHoy()" :disabled="store.cargando">
        Hoy
      </button>
      <div
        v-if="store.cargando"
        class="spinner-border spinner-border-sm text-verde ms-1"
      ></div>
    </div>

    <!-- Filtros -->
    <select
      :value="store.filtroMaquina"
      @change="store.setFiltroMaquina($event.target.value || null)"
      class="form-select select-barra"
    >
      <option value="">Todas las máquinas</option>
      <option v-for="m in maquinas" :key="m.id" :value="m.id">{{ m.nombre }}</option>
    </select>

    <select
      :value="store.filtroPeriodicidad"
      @change="store.setFiltroPeriodicidad($event.target.value)"
      class="form-select select-barra"
    >
      <option value="">Toda periodicidad</option>
      <option value="SEMANAL">Semanal</option>
      <option value="MENSUAL">Mensual</option>
      <option value="TRIMESTRAL">Trimestral</option>
      <option value="SEMESTRAL">Semestral</option>
      <option value="ANUAL">Anual</option>
    </select>

    <!-- Contadores — solo punto de color + número, detalle en el title -->
    <div class="contadores-mini">
      <button
        v-for="item in contadores"
        :key="item.estatus"
        :class="['mini-chip', { activo: esActivo(item.estatus) }]"
        @click="toggleContador(item.estatus)"
        :title="item.estatus ? `Filtrar: ${item.label}` : 'Ver todos'"
      >
        <span v-if="item.color" class="mini-dot" :style="{ background: item.color }"></span>
        <strong>{{ conteo(item.estatus) }}</strong>
      </button>
      <button
        v-if="hayFiltrosActivos"
        class="btn-limpiar"
        @click="limpiarTodo"
        title="Limpiar filtros"
      >
        <i class="bi bi-x-lg"></i>
      </button>
    </div>

    <div class="flex-spacer"></div>

    <!-- Programar recurrente (antes botón flotante) -->
    <button class="btn-recurrente" @click="emit('programar-recurrente')">
      <i class="bi bi-arrow-repeat"></i>
      <span class="btn-recurrente-texto">Programar recurrente</span>
    </button>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useCalendarioStore, COLOR_ESTATUS } from "@/store/calendarioStore";

defineProps({
  maquinas: { type: Array, default: () => [] },
});

const emit = defineEmits(["programar-recurrente"]);

const store = useCalendarioStore();

const mesTitulo = computed(() => {
  if (!store.mesActual) return "";
  const [anio, mes] = store.mesActual.split("-").map(Number);
  return new Date(anio, mes - 1, 1)
    .toLocaleDateString("es-MX", { month: "long", year: "numeric" })
    .replace(/^\w/, (c) => c.toUpperCase());
});

const contadores = [
  { estatus: "", label: "Total", color: null },
  { estatus: "PENDIENTE", label: "Pendientes", color: COLOR_ESTATUS.PENDIENTE.bg },
  { estatus: "EN_PROCESO", label: "En proceso", color: COLOR_ESTATUS.EN_PROCESO.bg },
  { estatus: "COMPLETADO", label: "Completados", color: COLOR_ESTATUS.COMPLETADO.bg },
  { estatus: "ATRASADO", label: "Atrasados", color: COLOR_ESTATUS.ATRASADO.bg },
];

// Contadores sobre gruposFiltrados para reflejar la vista actual
function conteo(estatus) {
  const base = store.gruposFiltrados;
  if (!estatus) return base.length;
  return base.filter((g) => g.estatus_grupo === estatus).length;
}

function esActivo(estatus) {
  if (!estatus) return false;
  return store.filtroEstatus === estatus;
}

function toggleContador(estatus) {
  if (!estatus) {
    store.filtroEstatus = "";
    return;
  }
  store.toggleFiltroEstatus(estatus);
}

const hayFiltrosActivos = computed(
  () => store.filtroMaquina || store.filtroPeriodicidad || store.filtroEstatus
);

function limpiarTodo() {
  store.setFiltroMaquina(null);
  store.setFiltroPeriodicidad("");
  store.filtroEstatus = "";
}
</script>

<style scoped>
/*
 * Se "rompe" fuera del padding de .main-content (AdminLayout.vue, 2rem en
 * escritorio / 1rem lateral en móvil) con márgenes negativos, para quedar
 * pegada al borde superior como una cinta — mismo tratamiento que
 * CrudToolbar.vue en el resto de las secciones ya rediseñadas.
 */
.barra-controles {
  background-color: var(--d-cream);
  border-bottom: 1px solid var(--d-border);
  min-height: 56px;
  box-sizing: border-box;
  margin: -2rem -2rem var(--d-space-4) -2rem;
  padding: 0.45rem 2rem;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

@media (max-width: 991.98px) {
  .barra-controles {
    min-height: 52px;
    /* Cancela también el colchón entre el navbar móvil (65px) y el
       padding-top de .main-content (85px) para quedar pegada arriba. */
    margin: -20px -1rem var(--d-space-4) -1rem;
    padding: var(--d-space-2) 1rem;
  }
}

.nav-mes {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
}

.mes-label {
  font-weight: 800;
  color: var(--d-brown);
  font-size: 0.88rem;
  min-width: 130px;
  text-align: center;
  white-space: nowrap;
}

.btn-nav {
  background: white;
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  border-radius: 6px;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  font-size: 0.78rem;
}
.btn-nav:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-nav:disabled {
  opacity: 0.35;
  cursor: default;
}

.btn-hoy {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 3px 10px;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;
}
.btn-hoy:hover:not(:disabled) {
  background-color: var(--d-green-dark);
}
.btn-hoy:disabled {
  opacity: 0.35;
}
.text-verde {
  color: var(--d-green);
}

.select-barra {
  background-color: white !important;
  border: 1px solid var(--d-green);
  color: var(--d-brown) !important;
  border-radius: 8px;
  font-size: 0.76rem;
  padding: 2px 22px 2px 8px;
  height: 27px;
  width: 148px;
  flex-shrink: 0;
}

/* ── Contadores compactos: solo punto + número, título trae el detalle ── */
.contadores-mini {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.mini-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 27px;
  padding: 0 8px;
  border-radius: var(--d-radius-pill);
  border: 1.5px solid var(--d-border-soft);
  background: white;
  color: var(--d-text-muted);
  font-size: 0.76rem;
  cursor: pointer;
  transition: all 0.18s;
}
.mini-chip:hover {
  border-color: var(--d-green);
}
.mini-chip.activo {
  background: var(--d-nav-hover-bg);
  border-color: var(--d-green);
  color: var(--d-brown);
}
.mini-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.btn-limpiar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 27px;
  height: 27px;
  background: none;
  border: 1px dashed rgba(64, 34, 8, 0.28);
  color: var(--d-brown);
  border-radius: 50%;
  font-size: 0.68rem;
  cursor: pointer;
  opacity: 0.65;
  transition: opacity 0.2s;
}
.btn-limpiar:hover {
  opacity: 1;
  background: rgba(64, 34, 8, 0.05);
}

.flex-spacer {
  flex: 1;
  min-width: 8px;
}

.btn-recurrente {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 14px;
  background-color: var(--d-green-dark);
  color: var(--d-cream);
  border: none;
  border-radius: var(--d-radius-pill);
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background-color 0.2s;
}
.btn-recurrente:hover {
  background-color: var(--d-brown);
}

@media (max-width: 991.98px) {
  .btn-recurrente-texto {
    display: none;
  }
  .btn-recurrente {
    width: 30px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
}

@media (max-width: 900px) {
  .select-barra {
    width: 132px;
  }
}
@media (max-width: 600px) {
  .barra-controles {
    justify-content: center;
  }
  .mes-label {
    min-width: 105px;
    font-size: 0.82rem;
  }
  .select-barra {
    width: 100%;
  }
  .flex-spacer {
    display: none;
  }
}
</style>
